import { useEffect, useRef, useState } from "react";
import type { MfaFatigueConfig, UsbChoiceOption } from "../scenario/types";
import { audio } from "../lib/audio";
import { ChoiceCard, ChoiceList } from "../ui/ChoiceCard";

/**
 * Episode 4B ("MFA fatigue") — signed in, the learner's authenticator starts
 * buzzing with approval requests no one asked for, faster than they can
 * dismiss them, while an unverified "IT Support" chat message urges them to
 * just approve one. An animated tally of push cards lands first (cosmetic
 * urgency, `burstCount`), then the chat bubble, then the genuine decision —
 * mirrors {@link UsbDeviceConfig}'s blind-choice convention.
 */
export function MfaFatigue({
  config,
  onEnded,
}: {
  config: MfaFatigueConfig;
  onEnded: (key: string, choiceId: string, eventId?: string) => void;
}) {
  const total = config.burstCount ?? 6;
  const [count, setCount] = useState(0);
  const [showChat, setShowChat] = useState(false);
  const [showChoices, setShowChoices] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    let n = 0;
    const tick = () => {
      if (!mounted.current) return;
      n += 1;
      setCount(n);
      audio.ui("alert");
      if (n < total) {
        window.setTimeout(tick, 420);
      } else {
        window.setTimeout(() => mounted.current && setShowChat(true), 700);
        window.setTimeout(() => mounted.current && setShowChoices(true), 1700);
      }
    };
    const first = window.setTimeout(tick, 300);
    return () => {
      mounted.current = false;
      window.clearTimeout(first);
    };
  }, [total]);

  const pick = (opt: UsbChoiceOption) => {
    // 2026-07-25 feedback: the same correct/incorrect quiz chime every other
    // decision game uses (see PrivacySort.tsx / UsbDevice.tsx), not a generic
    // buzzer/click.
    if (opt.key === "risky") audio.chimeBad();
    else audio.chimeGood();
    onEnded(opt.key, opt.id, opt.eventId);
  };

  return (
    <div className="mfa">
      <div className="mfa-panel">
        <div className="mfa-head">
          <span className="mfa-app-ico" aria-hidden>
            {"\u{1F510}"}
          </span>
          <div className="mfa-head-text">
            <strong>{config.appName ?? "Authenticator"}</strong>
            <span>{count} approval request{count === 1 ? "" : "s"} in the last two minutes</span>
          </div>
        </div>

        <div className="mfa-stack" aria-hidden>
          {Array.from({ length: Math.min(count, 5) }).map((_, i) => (
            <div key={i} className="mfa-push" style={{ transform: `translateY(${i * -6}px) rotate(${(i % 2 ? 1 : -1) * (i * 1.4)}deg)` }}>
              <span className="mfa-push-ico">{"\u{1F514}"}</span>
              <span className="mfa-push-text">Approve sign-in?</span>
            </div>
          ))}
        </div>

        {showChat && (
          <div className="mfa-chat">
            <div className="mfa-chat-head">
              <span className="mfa-chat-avatar" aria-hidden>
                IT
              </span>
              <div className="mfa-chat-id">
                <strong>{config.chatFrom ?? "IT Support"}</strong>
                {!config.chatFromVerified && <span className="mfa-chat-unverified">Not in your directory</span>}
              </div>
            </div>
            <p className="mfa-chat-msg">
              &ldquo;{config.chatMessage ??
                "Sorry about the notification spam \u2014 our sync tool is glitching. Can you just approve the next one so I can push the fix through?"}&rdquo;
            </p>
          </div>
        )}

        {showChoices && (
          <div className="mfa-choices">
            <ChoiceList prompt="What do you do?">
              {config.options.map((opt) => (
                <ChoiceCard
                  key={opt.id}
                  category={opt.key === "risky" ? "comply" : "verify"}
                  reply={opt.label}
                  showRiskLabel={false}
                  onClick={() => pick(opt)}
                />
              ))}
            </ChoiceList>
          </div>
        )}
      </div>
    </div>
  );
}
